import { useEffect, type ReactNode } from "react";
import { Navigate, NavLink, useParams } from "react-router-dom";

import { useDocumentPage, type DocumentListBody } from "../api/documents";
import { useCollections } from "../api/queries";
import { useAuth } from "../auth/auth-context";
import { DocumentList } from "../documents/DocumentList";
import { PageHeader } from "../layout/PageHeader";
import { cx } from "../lib/cx";
import { StorageKey, readStored, writeStored } from "../lib/storage";

/** Home sub-tabs are real routes; the last one used is remembered (note 03 §3.2). */
export const homeTabs = [
  { id: "viewed", label: "Recently viewed", path: "/home" },
  { id: "popular", label: "Popular", path: "/home/popular" },
  { id: "recent", label: "Recently updated", path: "/home/recent" },
  { id: "created", label: "Created by me", path: "/home/created" },
] as const;

export type HomeTabId = (typeof homeTabs)[number]["id"];

function isHomeTab(value: string | undefined): value is HomeTabId {
  return homeTabs.some((tab) => tab.id === value);
}

/**
 * Which call each tab makes. "Recently viewed" is its own endpoint because the
 * order is the caller's reading history rather than a column; the other three
 * are `documents.list` under a different sort.
 */
export function homeTabRequest(
  tab: HomeTabId,
  userId: string | undefined,
): { method: string; body: DocumentListBody } {
  switch (tab) {
    case "viewed":
      return { method: "documents.viewed", body: {} };
    case "popular":
      return { method: "documents.list", body: { sort: "popularityScore", direction: "DESC" } };
    case "recent":
      return { method: "documents.list", body: { sort: "updatedAt", direction: "DESC" } };
    case "created":
      return {
        method: "documents.list",
        body: {
          sort: "createdAt",
          direction: "DESC",
          ...(userId === undefined ? {} : { userId }),
        },
      };
  }
}

function HomeTabs(): ReactNode {
  return (
    <nav aria-label="Home tabs" className="mt-4 flex gap-1 border-b border-line">
      {homeTabs.map((tab) => (
        <NavLink
          key={tab.id}
          to={tab.path}
          end={tab.path === "/home"}
          className={({ isActive }) =>
            cx(
              "-mb-px border-b-2 px-3 py-2 text-sm",
              isActive ? "border-accent text-ink" : "border-transparent text-muted hover:text-ink",
            )
          }
        >
          {tab.label}
        </NavLink>
      ))}
    </nav>
  );
}

export function HomeScreen(): ReactNode {
  const { tab } = useParams();
  const { session, status } = useAuth();
  const active: HomeTabId = isHomeTab(tab) ? tab : "viewed";
  const request = homeTabRequest(active, session?.info.user.id);
  const page = useDocumentPage(request.method, request.body, status === "authenticated");
  const collections = useCollections(status === "authenticated");

  useEffect(() => {
    writeStored(StorageKey.homeTab, active);
  }, [active]);

  if (tab !== undefined && !isHomeTab(tab)) {
    return <Navigate to="/404" replace />;
  }

  const label = homeTabs.find((entry) => entry.id === active)?.label ?? "Home";

  return (
    <>
      <PageHeader>
        <h1 className="truncate text-base font-semibold">Home</h1>
      </PageHeader>
      <div className="max-w-3xl">
        <h2 className="text-2xl font-semibold">Home</h2>
        <HomeTabs />
        <DocumentList
          label={label}
          documents={page.data?.documents ?? []}
          abilities={page.data?.abilities ?? {}}
          collections={collections.data ?? []}
          loading={page.isPending}
          error={page.error}
          emptyMessage={
            active === "viewed"
              ? "Documents you open appear here."
              : active === "created"
                ? "You have not created any documents yet."
                : "Nothing here yet."
          }
        />
      </div>
    </>
  );
}

/** `/home` sends the visitor to whichever tab they used last. */
export function HomeRedirect(): ReactNode {
  const remembered = readStored(StorageKey.homeTab);
  if (isHomeTab(remembered ?? undefined) && remembered !== "viewed") {
    return <Navigate to={`/home/${remembered}`} replace />;
  }
  return <HomeScreen />;
}
